import { useEffect, useRef } from 'react';
import './urban-network-loader.css';

/**
 * UrbanNetworkLoader — W WESTEER, animación red de nodos.
 *
 * 30 nodos dispersos convergen hacia la W alámbrica con glow cyan.
 * Timing: 3 s convergencia + 1 s hold → loop.
 *
 * Geometría derivada de WESTEER/05_MARCA/W/W_network_anim.svg.
 * viewBox crop de la región de la W: 110 260 1040 625 (ratio ≈ 1.66:1).
 */

export interface UrbanNetworkLoaderProps {
  size?: 'xs' | 'sm' | 'md' | 'xl';
  className?: string;
}

// ── Timing ────────────────────────────────────────────────────────
const MOVE = 3000;  // ms convergiendo
const HOLD = 1000;  // ms sosteniendo la W

// ── Posiciones finales de los 30 nodos ────────────────────────────
const FP: [number, number][] = [
  [472.5,  853.5 ], [382.5,  695    ], [581,    686.5  ], [433.25, 608.5  ], [484,    522    ],
  [371,    672    ], [472.5,  506    ], [363,    485.5  ], [268.75, 485.5  ], [355,    299    ],
  [166.5,  299    ], [496.5,  506    ], [589.5,  672    ], [627,    602    ], [627,    299    ],
  [695.5,  402.5  ], [669.5,  672    ], [764,    506    ],
  [787.256,854    ], [678.847,686.849], [778.91, 613    ], [832.218,774.679],
  [877.181,695.357], [775.766,522.201], [787.256,506.187],
  [888.671,672.336], [939.753,579.002], [904.658,299    ],
  [990.836,485.668], [1093,   299    ],
];

// ── 50 aristas [índice nodo A, índice nodo B] ─────────────────────
const EI: [number, number][] = [
  [0,1],[0,2],[0,3],[1,3],[1,2],[2,4],[4,3],[2,3],
  [5,6],[5,7],[5,8],[6,9],[6,7],[9,10],[9,7],[10,7],[10,8],[7,8],
  [11,12],[12,13],[11,14],[11,15],[11,13],[11,17],
  [13,16],[16,17],[13,17],[13,14],[17,15],[14,15],
  [18,19],[18,20],[18,21],[22,23],[22,21],[22,20],
  [23,19],[23,20],[19,20],[19,21],
  [24,25],[25,26],[24,27],[24,28],[24,26],[24,29],
  [27,29],[27,28],[29,28],[28,26],
];

// ── Radios de nodos ───────────────────────────────────────────────
const OUTER_R = [12,12,12,11,11,12,12,11,11,12,14,12,11,12,14,11,11,12,14,12,11,12,12,11,12,11,11,12,11,14];
const INNER_R = [6,5.5,5.5,5,5,5.5,5.5,5,5,5.5,7,5.5,5,5.5,7,5,5,5.5,7,5.5,5,5.5,5.5,5,5.5,5,5,5.5,5,7];

function easeOut(t: number): number {
  return 1 - Math.pow(1 - Math.min(1, t), 3);
}

export function UrbanNetworkLoader({
  size = 'md',
  className,
}: UrbanNetworkLoaderProps): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const cls = ['unl', `unl--${size}`, className].filter(Boolean).join(' ');

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const el = svg.querySelectorAll<SVGLineElement>('.unl__el');
    const no = svg.querySelectorAll<SVGCircleElement>('.unl__no');
    const ni = svg.querySelectorAll<SVGCircleElement>('.unl__ni');

    let rafId = 0;
    let sc: [number, number][] = [];

    function scatter(): void {
      sc = FP.map((_, i) => {
        const a = i * 2.39996323; // ángulo áureo
        const r = 270 + ((i * 53) % 230);
        const x = 627 + Math.cos(a) * r;
        const y = 577 + Math.sin(a) * r * 0.78;
        return [Math.max(50, Math.min(1204, x)), Math.max(50, Math.min(1204, y))];
      });
    }

    function setNode(i: number, x: number, y: number): void {
      no[i].setAttribute('cx', String(x));
      no[i].setAttribute('cy', String(y));
      ni[i].setAttribute('cx', String(x));
      ni[i].setAttribute('cy', String(y));
    }

    function cycle(): void {
      scatter();

      for (let i = 0; i < 30; i++) setNode(i, sc[i][0], sc[i][1]);

      for (let j = 0; j < EI.length; j++) {
        el[j].setAttribute('x1', String(sc[EI[j][0]][0]));
        el[j].setAttribute('y1', String(sc[EI[j][0]][1]));
        el[j].setAttribute('x2', String(sc[EI[j][1]][0]));
        el[j].setAttribute('y2', String(sc[EI[j][1]][1]));
        el[j].setAttribute('stroke-opacity', '1');
      }

      let t0: number | null = null;

      function frame(ts: number): void {
        if (t0 === null) t0 = ts;
        const e = ts - t0;

        if (e >= MOVE + HOLD) {
          cycle();
          return;
        }

        const t = easeOut(e / MOVE);

        const cp: [number, number][] = FP.map((f, i) => [
          sc[i][0] + (f[0] - sc[i][0]) * t,
          sc[i][1] + (f[1] - sc[i][1]) * t,
        ]);

        for (let i = 0; i < 30; i++) setNode(i, cp[i][0], cp[i][1]);

        for (let j = 0; j < EI.length; j++) {
          const a = EI[j][0], b = EI[j][1];
          el[j].setAttribute('x1', String(cp[a][0]));
          el[j].setAttribute('y1', String(cp[a][1]));
          el[j].setAttribute('x2', String(cp[b][0]));
          el[j].setAttribute('y2', String(cp[b][1]));
        }

        rafId = requestAnimationFrame(frame);
      }

      rafId = requestAnimationFrame(frame);
    }

    cycle();

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <svg
      ref={svgRef}
      viewBox="110 260 1040 625"
      className={cls}
      focusable="false"
      aria-hidden="true"
      overflow="visible"
    >
      <defs>
        <filter id="unl-seg-glow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.8" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="unl-node-glow" x="-400%" y="-400%" width="900%" height="900%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="b1" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="b2" />
          <feMerge>
            <feMergeNode in="b1" />
            <feMergeNode in="b2" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* 50 aristas */}
      <g stroke="#2ab8e8" strokeWidth="1.4" fill="none" strokeLinecap="round" filter="url(#unl-seg-glow)">
        {Array.from({ length: 50 }, (_, i) => (
          <line key={i} className="unl__el" x1="0" y1="0" x2="0" y2="0" strokeOpacity={1} />
        ))}
      </g>

      {/* 30 nodos outer glow */}
      <g fill="#18a0d0" opacity={0.55} filter="url(#unl-node-glow)">
        {OUTER_R.map((r, i) => (
          <circle key={i} className="unl__no" cx="627" cy="577" r={r} />
        ))}
      </g>

      {/* 30 nodos inner bright */}
      <g fill="#c8f2ff">
        {INNER_R.map((r, i) => (
          <circle key={i} className="unl__ni" cx="627" cy="577" r={r} />
        ))}
      </g>
    </svg>
  );
}
